// src/components/filters/SearchFilter.tsx
import React, { useState, useEffect } from 'react';

interface SearchFilterProps {
  searchQuery: string | null;
  onSearchChange: (query: string | null) => void;
  debounceMs?: number;
}

const SearchFilter: React.FC<SearchFilterProps> = ({ 
  searchQuery, 
  onSearchChange,
  debounceMs = 300
}) => {
  // Local state for the input value (for immediate UI feedback)
  const [inputValue, setInputValue] = useState(searchQuery || '');
  
  // Debounce the search query updates
  useEffect(() => {
    const timer = setTimeout(() => {
      // Only call onSearchChange if the value has actually changed
      const trimmedValue = inputValue.trim();
      const newQuery = trimmedValue === '' ? null : trimmedValue;
      
      if (newQuery !== searchQuery) {
        onSearchChange(newQuery);
      }
    }, debounceMs);
    
    return () => clearTimeout(timer);
  }, [inputValue, debounceMs, onSearchChange, searchQuery]);
  
  // Sync local state when external searchQuery changes (e.g., from reset)
  useEffect(() => {
    setInputValue(searchQuery || '');
  }, [searchQuery]);
  
  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };
  
  // Handle clear button
  const handleClear = () => {
    setInputValue('');
    onSearchChange(null);
  };
  
  return (
    <div className="search-filter">
      <label htmlFor="search-input">Search:</label>
      <div className="search-input-wrapper" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <input
          id="search-input"
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          placeholder="Search by gathering or owner name..."
          className="search-input"
          style={{ paddingRight: inputValue ? '35px' : '10px', flex: 1 }}
        />
        {inputValue && (
          <button 
            className="clear-search-button"
            onClick={handleClear}
            aria-label="Clear search"
            type="button"
            style={{
              position: 'absolute',
              right: '8px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: '18px',
              color: '#666',
              padding: '4px 8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
};

export default SearchFilter;
